/**
 * LoginShell — Outer-Wrapper + RoleHeader + Switch-Link fuer die vier
 * Submit-Formulare. Extrahiert aus `pages/Login.tsx`.
 *
 * Liefert die einheitliche Karten-Hülle (`bg-card p-6 rounded-2xl
 * shadow-card`), die in den ungekapselten Forms 4× identisch wiederholt
 * wurde, plus den oberen Mode-Badge und den unteren
 * "Bereits ein Konto? Anmelden / Noch kein Konto?"-Link.
 */

import type { ReactNode } from 'react';
import { ArrowLeft, Building2, Heart, Stethoscope } from 'lucide-react';
import type { SettingType } from '@/hooks/useFeatures';

interface Props {
  role: 'fachperson' | 'patient';
  mode: SettingType | null;
  isRegister: boolean;
  /**
   * Klick auf den Sign-in/Sign-up-Toggle. Wenn `undefined`, blendet die
   * Shell den Toggle aus — für Workspaces ohne Self-Service-Signup
   * (z. B. Plattform-Admin, der nur manuell angelegt wird).
   */
  onSwitchMode?: () => void;
  /**
   * Klick auf "Wechseln" oben rechts. Springt zurueck zum
   * Workspace-Picker (`/login`).
   */
  onChangeRole: () => void;
  children: ReactNode;
}

export function LoginShell({
  role,
  mode,
  isRegister,
  onSwitchMode,
  onChangeRole,
  children,
}: Props) {
  const modeLabel = mode === 'klinik' ? 'Klinik / Stationär' : 'Ambulant / Praxis';

  return (
    <div className="space-y-4 bg-card p-6 rounded-2xl shadow-card">
      <div className="flex items-center gap-3 pb-3 border-b border-border">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            role === 'fachperson' ? 'bg-primary/10' : 'bg-green-100'
          }`}
        >
          {role === 'fachperson' ? (
            <Stethoscope className="w-4 h-4 text-primary" />
          ) : (
            <Heart className="w-4 h-4 text-green-700" />
          )}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-sm font-medium text-foreground">
            {role === 'fachperson' ? 'Fachperson' : 'Patient'}
          </span>
          {role === 'fachperson' && mode && (
            <span
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                mode === 'klinik'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-primary/10 text-primary'
              }`}
            >
              {mode === 'klinik' ? (
                <Building2 className="w-2.5 h-2.5" />
              ) : (
                <Stethoscope className="w-2.5 h-2.5" />
              )}
              {modeLabel}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onChangeRole}
          className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors shrink-0"
        >
          <ArrowLeft className="w-3 h-3" />
          Wechseln
        </button>
      </div>

      {children}

      {onSwitchMode && (
        <button
          type="button"
          onClick={onSwitchMode}
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {isRegister
            ? 'Bereits ein Konto? Anmelden'
            : 'Noch kein Konto? Registrieren'}
        </button>
      )}
    </div>
  );
}
